import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import LoadingTableBox from '../LoadingTableBox';
import MessageBox from '../MessageBox';

/**
 * `.panel > table.data` from the entity-list spec. Rows are keyboard-reachable
 * (tabIndex + Enter/Space) when `onRowClick` is set — closing the a11y gap the
 * spec calls out in the reference mock.
 */
// Deterministic per-cell widths for skeleton bars — avoids every column
// rendering the exact same bar width, without relying on Math.random()
// (which would reshuffle on every re-render).
const SKELETON_WIDTHS = ['tw:w-4/5', 'tw:w-3/5', 'tw:w-2/3', 'tw:w-1/2', 'tw:w-3/4'];

function DataTable({
	columns,
	rows,
	loading,
	error,
	sortColumn,
	direction,
	onSort,
	onRowClick,
	rowActions,
	emptyMessage = 'No se encontraron resultados.',
	rowKey = (row) => row._id,
	footerRow,
	bare = false,
	selection,
	skeleton = false,
	skeletonRows = 8,
	rowClassName,
}) {
	const colSpan = columns.length + (rowActions ? 1 : 0) + (selection ? 1 : 0);
	const showEmpty = !loading && !error && rows.length === 0;
	const showSkeleton = loading && skeleton;
	const showLoader = loading && !skeleton;

	const isSelectable = selection?.isSelectable || (() => true);
	const selectableRows = selection ? rows.filter(isSelectable) : [];
	const selectedCount = selection ? selectableRows.filter((row) => selection.selectedIds.includes(rowKey(row))).length : 0;
	const headerCheckState =
		selectableRows.length === 0 || selectedCount === 0
			? 'unchecked'
			: selectedCount === selectableRows.length
				? 'checked'
				: 'mixed';

	const handleCheckboxKeyDown = (e, onActivate) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onActivate();
		}
	};

	const table = (
		<div className='tw:overflow-x-auto'>
			{selection?.bulkBar}
			<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
				<thead>
					<tr>
						{selection && (
							<th className='tw:w-[40px] tw:pr-0 tw:py-2.5 tw:pl-4 tw:bg-surface-2 tw:border-b tw:border-border'>
								<span
									role='checkbox'
									aria-checked={headerCheckState === 'mixed' ? 'mixed' : headerCheckState === 'checked'}
									tabIndex={0}
									onClick={selection.onToggleAll}
									onKeyDown={(e) => handleCheckboxKeyDown(e, selection.onToggleAll)}
									className={classnames(
										'tw:inline-grid tw:place-items-center tw:w-[17px] tw:h-[17px] tw:rounded-[4.5px] tw:border-[1.5px] tw:cursor-pointer tw:transition-colors tw:duration-100',
										headerCheckState === 'unchecked'
											? 'tw:border-border-strong tw:bg-surface'
											: 'tw:border-accent tw:bg-accent',
									)}
								>
									{headerCheckState === 'checked' && (
										<svg viewBox='0 0 16 16' className='tw:w-[11px] tw:h-[11px] tw:fill-white'>
											<path d='M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z' />
										</svg>
									)}
									{headerCheckState === 'mixed' && <span className='tw:w-[9px] tw:h-[2px] tw:bg-white tw:rounded-full' />}
								</span>
							</th>
						)}
						{columns.map((column) => (
							<th
								key={column.key}
								onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
								className={classnames(
									'tw:px-4 tw:py-2.5 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3 tw:bg-surface-2 tw:border-b tw:border-border tw:whitespace-nowrap',
									column.sortable && 'tw:cursor-pointer',
									column.className,
								)}
							>
								{column.header}
								{column.sortable && column.key === sortColumn && (
									<span className='tw:inline-block tw:ml-1 tw:align-middle'>
										{direction === 'desc' ? <FaSortUp /> : <FaSortDown />}
									</span>
								)}
							</th>
						))}
						{rowActions && <th className='tw:w-[36px] tw:bg-surface-2 tw:border-b tw:border-border' />}
					</tr>
				</thead>
				<tbody>
					{showLoader && (
						<tr>
							<td colSpan={colSpan}>
								<LoadingTableBox loading={loading} />
							</td>
						</tr>
					)}
					{showSkeleton &&
						Array.from({ length: skeletonRows }, (_, rowIndex) => (
							<tr key={`skeleton-${rowIndex}`} className='tw:border-b tw:border-border tw:last:border-b-0'>
								{selection && (
									<td className='tw:w-[40px] tw:pr-0 tw:pl-4 tw:py-3'>
										<span className='tw:block tw:w-[17px] tw:h-[17px] tw:rounded-[4.5px] tw:bg-surface-3 tw:animate-pulse' />
									</td>
								)}
								{columns.map((column, colIndex) => (
									<td key={column.key} className={classnames('tw:px-4 tw:py-3', column.className)}>
										<span
											className={classnames(
												'tw:block tw:h-[13px] tw:rounded tw:bg-surface-3 tw:animate-pulse',
												SKELETON_WIDTHS[(rowIndex + colIndex) % SKELETON_WIDTHS.length],
											)}
										/>
									</td>
								))}
								{rowActions && (
									<td className='tw:px-4 tw:py-3'>
										<span className='tw:block tw:w-5 tw:h-[13px] tw:rounded tw:bg-surface-3 tw:animate-pulse tw:ml-auto' />
									</td>
								)}
							</tr>
						))}
					{!loading && error && (
						<tr>
							<td colSpan={colSpan}>
								<MessageBox variant='danger'>{error}</MessageBox>
							</td>
						</tr>
					)}
					{showEmpty && (
						<tr>
							<td colSpan={colSpan} className='tw:px-12 tw:py-12 tw:text-center tw:text-ink-3'>
								{emptyMessage}
							</td>
						</tr>
					)}
					{!loading &&
							!error &&
							rows.map((row) => {
								const id = rowKey(row);
								const selectable = selection ? isSelectable(row) : false;
								const checked = selection ? selection.selectedIds.includes(id) : false;
								return (
									<tr
										key={id}
										tabIndex={onRowClick ? 0 : undefined}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										onKeyDown={
											onRowClick
												? (e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														onRowClick(row);
													}
												}
												: undefined
										}
										className={classnames(
											'tw:border-b tw:border-border tw:transition-colors tw:duration-100 tw:last:border-b-0',
											onRowClick && 'tw:cursor-pointer tw:hover:bg-surface-2',
											checked && 'tw:bg-accent-soft',
											rowClassName && rowClassName(row),
										)}
									>
										{selection && (
											<td
												className='tw:w-[40px] tw:pr-0 tw:pl-4 tw:py-2.5 tw:align-middle'
												onClick={(e) => e.stopPropagation()}
											>
												<span
													role='checkbox'
													aria-checked={checked}
													aria-disabled={!selectable}
													tabIndex={selectable ? 0 : undefined}
													onClick={selectable ? () => selection.onToggle(id) : undefined}
													onKeyDown={selectable ? (e) => handleCheckboxKeyDown(e, () => selection.onToggle(id)) : undefined}
													className={classnames(
														'tw:inline-grid tw:place-items-center tw:w-[17px] tw:h-[17px] tw:rounded-[4.5px] tw:border-[1.5px] tw:transition-colors tw:duration-100',
														!selectable
															? 'tw:opacity-35 tw:cursor-not-allowed tw:border-border-strong tw:bg-surface'
															: checked
																? 'tw:cursor-pointer tw:border-accent tw:bg-accent'
																: 'tw:cursor-pointer tw:border-border-strong tw:bg-surface',
													)}
												>
													{checked && selectable && (
														<svg viewBox='0 0 16 16' className='tw:w-[11px] tw:h-[11px] tw:fill-white'>
															<path d='M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z' />
														</svg>
													)}
												</span>
											</td>
										)}
										{columns.map((column) => (
											<td
												key={column.key}
												className={classnames('tw:px-4 tw:py-2.5 tw:text-[13.5px] tw:align-middle tw:text-ink-0', column.className)}
											>
												{column.render ? column.render(row) : row[column.key]}
											</td>
										))}
										{rowActions && (
											<td
												className='tw:px-4 tw:py-2.5 tw:align-middle tw:text-right'
												onClick={(e) => e.stopPropagation()}
											>
												{rowActions(row)}
											</td>
										)}
									</tr>
								);
							})}
				</tbody>
				{footerRow && (
					<tfoot>
						<tr className='tw:border-t-2 tw:border-border'>
							{selection && <td />}
							{columns.map((column) => (
								<td
									key={column.key}
									className={classnames('tw:px-4 tw:py-2.5 tw:text-[13.5px] tw:align-middle tw:text-ink-0 tw:font-semibold', column.className)}
								>
									{column.footerRender ? column.footerRender(footerRow) : column.render ? column.render(footerRow) : footerRow[column.key]}
								</td>
							))}
							{rowActions && <td />}
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	);

	if (bare) {
		return table;
	}

	return (
		<section className='tw:bg-surface tw:border tw:border-border tw:rounded-[12px] tw:overflow-hidden'>
			{table}
		</section>
	);
}

DataTable.propTypes = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			header: PropTypes.node,
			className: PropTypes.string,
			sortable: PropTypes.bool,
			render: PropTypes.func,
			footerRender: PropTypes.func,
		}),
	).isRequired,
	rows: PropTypes.array.isRequired,
	loading: PropTypes.bool,
	error: PropTypes.any,
	sortColumn: PropTypes.string,
	direction: PropTypes.string,
	onSort: PropTypes.func,
	onRowClick: PropTypes.func,
	rowActions: PropTypes.func,
	emptyMessage: PropTypes.node,
	rowKey: PropTypes.func,
	// A totals-style row rendered in a <tfoot>, styled distinctly from data
	// rows. Shaped like a data row; columns fall back to `render`/raw value
	// when no `footerRender` is given.
	footerRow: PropTypes.object,
	// When true, skip the outer bordered/rounded panel and render just the
	// table — for callers that supply their own single outer panel (e.g. one
	// that also has a header above the table).
	bare: PropTypes.bool,
	// Opt-in multi-select: adds a checkbox column + tri-state header checkbox.
	// Absent by default, so every existing caller renders unchanged.
	selection: PropTypes.shape({
		selectedIds: PropTypes.array.isRequired,
		onToggle: PropTypes.func.isRequired,
		onToggleAll: PropTypes.func.isRequired,
		isSelectable: PropTypes.func,
		bulkBar: PropTypes.node,
	}),
	// When true, the loading state renders skeleton placeholder rows instead
	// of the spinner-based LoadingTableBox. Opt-in per table so existing
	// callers keep the spinner until migrated.
	skeleton: PropTypes.bool,
	// Number of placeholder rows to render while `skeleton` is active.
	// Pass the page's row-per-page size so the skeleton matches the real
	// table height instead of guessing.
	skeletonRows: PropTypes.number,
	// Optional per-row extra className, e.g. dimming inactive rows.
	rowClassName: PropTypes.func,
};

export default DataTable;
